import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavBar } from '../../components/navBar.tsx';
import { PageTitle } from '../../components/pageTitle.tsx';
import { trpc } from '../../utils/trpc/trpc.ts';
import { AvailabilityGraph } from './availability-graph/availabilityGraph.tsx';
import { ConfirmedScrapsGraph } from './confirmed-scraps-graph/confirmedScrapsGraph.tsx';
import { MeasurementCountsGraph } from './measurement-counts-graph/measurementCountsGraph.tsx';
import { MeasurementDelaysGraph } from './measurement-delays-graph/measurementDelaysGraph.tsx';
import { getPeriodOptionSet } from './period-selection/createPeriodOptions.ts';
import { ReportControls } from './report-settings/reportControls.tsx';
import { ReportSettings } from './report-settings/reportSettings.ts';
import { ReportTypeInfoDialog } from './report-settings/reportTypeInfoDialog.tsx';
import { useReportSettings } from './report-settings/useReportSettings.ts';
import { ReportGraphLoading } from './reportGraphContainer.tsx';
import { ReportOperatorsDialog } from './reportOperatorsDialog.tsx';
import { ScrapsRatioGraph } from './scraps-ratio-graph/scrapsRatioGraph.tsx';
import { ScrapsTimeGraph } from './scrapsTimeGraph.tsx';
import { SetupsGraph } from './setups-graph/setupsGraph.tsx';
import { MeasurementShiftDelaysGraph } from './shift-delays-graph/measurementShiftDelaysGraph.tsx';


export function ReportGraphs() {

    const periodOptionSet = useMemo(getPeriodOptionSet, []);
    const { reportSettings, setReportSettings } = useReportSettings(periodOptionSet);
    const [isReportTypeInfoOpen, setIsReportTypeInfoOpen] = useState(false);
    const [operatorsDialogPeriodIndex, setOperatorsDialogPeriodIndex] = useState<number | null>(null);
    const [graphScrollEdges, setGraphScrollEdges] = useState({ left: false, right: false });
    const [isApplyingReportSettings, setIsApplyingReportSettings] = useState(false);
    const graphScrollRef = useRef<HTMLDivElement>(null);
    const pendingReportSettingsRef = useRef<ReportSettings | null>(null);
    const settingsAnimationFrameRef = useRef<number | null>(null);
    const settingsTimeoutRef = useRef<number | null>(null);
    const shouldShowOperatorCounts =
        reportSettings.reportType !== 'Shift delays'
        && reportSettings.reportType !== 'Setups'
        && reportSettings.resolution === 'Days'
        && reportSettings.showOperators;
    const { data: operatorCounts, isFetching: isOperatorCountsFetching } = trpc.operators.getOperatorCountsForReport.useQuery({
        periodStarts: reportSettings.periodsInRange.map((period) => period.start),
    }, {
        enabled: shouldShowOperatorCounts,
        refetchOnWindowFocus: false,
    });
    const operatorFooterLabelsByPeriod = useMemo(() => {
        if (!shouldShowOperatorCounts || !operatorCounts) return undefined;
        return operatorCounts.map((period) => period.counts.map((shiftCount) => `${shiftCount.shiftTeam} ${shiftCount.count}`));
    }, [operatorCounts, shouldShowOperatorCounts]);
    const handleOperatorFooterClick = useCallback((periodIndex: number) => {
        setOperatorsDialogPeriodIndex(periodIndex);
    }, []);
    const handleReportSettingsChange = useCallback((nextReportSettings: ReportSettings) => {
        pendingReportSettingsRef.current = nextReportSettings;
        setIsApplyingReportSettings(true);
        if (settingsAnimationFrameRef.current != null) {
            window.cancelAnimationFrame(settingsAnimationFrameRef.current);
        }
        if (settingsTimeoutRef.current != null) {
            window.clearTimeout(settingsTimeoutRef.current);
        }
        settingsAnimationFrameRef.current = window.requestAnimationFrame(() => {
            settingsTimeoutRef.current = window.setTimeout(() => {
                const pendingSettings = pendingReportSettingsRef.current;
                pendingReportSettingsRef.current = null;
                if (pendingSettings) {
                    setReportSettings(pendingSettings);
                }
            }, 0);
        });
    }, [setReportSettings]);
    const selectedOperatorsDialogPeriod =
        operatorsDialogPeriodIndex == null ? null : reportSettings.periodsInRange[operatorsDialogPeriodIndex] ?? null;
    const graphMinWidth = shouldShowOperatorCounts
        ? Math.max(700, reportSettings.periodsInRange.length * 100)
        : 0;
    const shouldShowGraphLoading = isApplyingReportSettings || (shouldShowOperatorCounts && isOperatorCountsFetching);
    useEffect(() => {
        const element = graphScrollRef.current;
        if (!element) return;
        const updateScrollEdges = () => {
            const maxScrollLeft = element.scrollWidth - element.clientWidth;
            setGraphScrollEdges({
                left: element.scrollLeft > 2,
                right: maxScrollLeft - element.scrollLeft > 2,
            });
        };
        const handleWheel = (event: WheelEvent) => {
            if (element.scrollWidth <= element.clientWidth || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
            element.scrollLeft += event.deltaY;
            event.preventDefault();
        };
        const resizeObserver = new ResizeObserver(updateScrollEdges);
        const mutationObserver = new MutationObserver(updateScrollEdges);

        element.addEventListener('wheel', handleWheel, { passive: false });
        element.addEventListener('scroll', updateScrollEdges, { passive: true });
        resizeObserver.observe(element);
        if (element.firstElementChild) resizeObserver.observe(element.firstElementChild);
        mutationObserver.observe(element, { childList: true, subtree: true });
        updateScrollEdges();

        return () => {
            element.removeEventListener('wheel', handleWheel);
            element.removeEventListener('scroll', updateScrollEdges);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, []);
    useEffect(() => {
        return () => {
            if (settingsAnimationFrameRef.current != null) {
                window.cancelAnimationFrame(settingsAnimationFrameRef.current);
            }
            if (settingsTimeoutRef.current != null) {
                window.clearTimeout(settingsTimeoutRef.current);
            }
        };
    }, []);
    useEffect(() => {
        if (!isApplyingReportSettings || pendingReportSettingsRef.current) return;
        const timeoutId = window.setTimeout(() => setIsApplyingReportSettings(false), 120);
        return () => window.clearTimeout(timeoutId);
    }, [isApplyingReportSettings, reportSettings]);

    return (<>
        <PageTitle title='Reporting' />
        <NavBar />
        <div className='content report-graphs'>
            <div className='report-graphs__body'>
                {/* Options */}
                <div className='report-graphs__options'>
                    <ReportControls
                        reportSettings={reportSettings}
                        setReportSettings={handleReportSettingsChange}
                        periodOptionSet={periodOptionSet}
                        onShowReportTypeInfo={() => setIsReportTypeInfoOpen(true)}
                    />
                </div>

                {/* Graphs */}
                <div className='report-graphs__chart-frame'>
                    <div ref={graphScrollRef} className='report-graphs__chart'>
                        <div style={{ height: '100%', minWidth: graphMinWidth }}>
                            {reportSettings.reportType === 'Availability' &&
                                <AvailabilityGraph
                                    reportSettings={reportSettings}
                                    operatorFooterLabelsByPeriod={operatorFooterLabelsByPeriod}
                                    onOperatorFooterClick={shouldShowOperatorCounts ? handleOperatorFooterClick : undefined}
                                />
                            }

                            {reportSettings.reportType === 'Scraps' &&
                                <ScrapsRatioGraph
                                    reportSettings={reportSettings}
                                    operatorFooterLabelsByPeriod={operatorFooterLabelsByPeriod}
                                    onOperatorFooterClick={shouldShowOperatorCounts ? handleOperatorFooterClick : undefined}
                                />
                            }

                            {reportSettings.reportType === 'Confirmed scrap ratio' &&
                                <ConfirmedScrapsGraph
                                    reportSettings={reportSettings}
                                    operatorFooterLabelsByPeriod={operatorFooterLabelsByPeriod}
                                    onOperatorFooterClick={shouldShowOperatorCounts ? handleOperatorFooterClick : undefined}
                                />
                            }

                            {reportSettings.reportType === 'Potential scraps time spent' &&
                                <ScrapsTimeGraph
                                    reportSettings={reportSettings}
                                    operatorFooterLabelsByPeriod={operatorFooterLabelsByPeriod}
                                    onOperatorFooterClick={shouldShowOperatorCounts ? handleOperatorFooterClick : undefined}
                                />
                            }

                            {reportSettings.reportType === 'Measurement counts' &&
                                <MeasurementCountsGraph
                                    reportSettings={reportSettings}
                                    operatorFooterLabelsByPeriod={operatorFooterLabelsByPeriod}
                                    onOperatorFooterClick={shouldShowOperatorCounts ? handleOperatorFooterClick : undefined}
                                />
                            }

                            {reportSettings.reportType === 'Measurement delays' &&
                                <MeasurementDelaysGraph
                                    reportSettings={reportSettings}
                                    operatorFooterLabelsByPeriod={operatorFooterLabelsByPeriod}
                                    onOperatorFooterClick={shouldShowOperatorCounts ? handleOperatorFooterClick : undefined}
                                />
                            }

                            {reportSettings.reportType === 'Shift delays' &&
                                <MeasurementShiftDelaysGraph reportSettings={reportSettings} />
                            }
                            {reportSettings.reportType === 'Setups' &&
                                <SetupsGraph reportSettings={reportSettings} />
                            }
                        </div>
                    </div>
                    <div
                        className={`report-graphs__scroll-fade report-graphs__scroll-fade--left${graphScrollEdges.left ? ' visible' : ''}`}
                        aria-hidden='true'
                    />
                    <div
                        className={`report-graphs__scroll-fade report-graphs__scroll-fade--right${graphScrollEdges.right ? ' visible' : ''}`}
                        aria-hidden='true'
                    />
                    {shouldShowGraphLoading && <ReportGraphLoading />}
                </div>
            </div>
        </div>
        {isReportTypeInfoOpen && (
            <ReportTypeInfoDialog
                selectedReportType={reportSettings.reportType}
                onClose={() => setIsReportTypeInfoOpen(false)}
            />
        )}
        {selectedOperatorsDialogPeriod && (
            <ReportOperatorsDialog period={selectedOperatorsDialogPeriod} onClose={() => setOperatorsDialogPeriodIndex(null)} />
        )}
    </>);
}
